<template>
  <div>
    <v-card class="mx-auto" max-width="500">
      <v-card-title class="title font-weight-regular justify-space-between">
        <span>{{ currentTitle }}</span>
      </v-card-title>

      <v-window v-model="step">
        <v-window-item :value="1">
          <v-card-text>
            <v-text-field
              label="Email"
              value
              v-model="userEmail"
              :error-messages="emailErrors"
              required
              @input="$v.userEmail.$touch()"
              @blur="$v.userEmail.$touch()"
              v-on:keyup.enter="$refs.nextStepButton.$el.click()"
              ref="userEmailInputField"
            ></v-text-field>
            <span class="caption grey--text text--darken-1">
              If we have this email address on the record, we will send reset
              password link to it.
            </span>
          </v-card-text>
        </v-window-item>

        <v-window-item :value="2">
          <v-card-text>
            <v-text-field
              label="Password"
              type="password"
              v-model="userPassword"
              :error-messages="passwordErrors"
              required
              @input="$v.userPassword.$touch()"
              @blur="$v.userPassword.$touch()"
              ref="userPasswordInputField"
            ></v-text-field>
            <v-text-field
              label="Confirm Password"
              type="password"
              v-model="userPasswordConfirm"
              :error-messages="confirmPasswordErrors"
              required
              @input="$v.userPasswordConfirm.$touch()"
              @blur="$v.userPasswordConfirm.$touch()"
              v-on:keyup.enter="$refs.nextStepButton.$el.click()"
            ></v-text-field>
            <span class="caption grey--text text--darken-1"
              >Please enter a new password for your account</span
            >
          </v-card-text>
        </v-window-item>

        <v-window-item :value="3">
          <div v-if="processLoaderIsVisible" class="pa-3 text-xs-center">
            <v-progress-circular
              :size="50"
              color="primary"
              indeterminate
            ></v-progress-circular>
          </div>
          <div v-else class="pa-3 text-xs-center">
            <div v-if="processType === 'send-login-reset-link'">
              <h3 class="title font-weight-light mb-2">
                We have sent an email with reset link to {{ userEmail }}
              </h3>
              <span class="caption grey&#45;&#45;text">
                Please check your email, we have sent you an email with account
                confirmation link...
              </span>
            </div>
            <div v-else-if="processType === 'reset-login-password'">
              <h3 class="title font-weight-light mb-2">
                {{ successMsg }}
              </h3>
              <span class="caption grey&#45;&#45;text">
                Please use the new password to login...
              </span>
            </div>
          </div>
        </v-window-item>
      </v-window>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          :disabled="nextButtonDisabled"
          color="primary"
          depressed
          @click="stepSkip('next')"
          ref="nextStepButton"
          >{{ nextButtonLabel }}</v-btn
        >
      </v-card-actions>
    </v-card>
    <ErrorDialog
      v-if="$store.state.errors.length > 0"
      :showDialog="$store.state.errors.length > 0"
      :errors="$store.state.errors"
      @clickedOk="onClickOkErrorDialog"
    ></ErrorDialog>
  </div>
</template>

<script>
import { validationMixin } from 'vuelidate'
import { required, minLength, email, sameAs } from 'vuelidate/lib/validators'
import ErrorDialog from '../components/ErrorDialog'

// const equalToPassword = (value) => {
//   return value===this.userPassword
// }

export default {
  mixins: [validationMixin],
  components: {
    ErrorDialog
  },
  data: () => ({
    step: 1,
    userEmail: '',
    userPassword: '',
    userPasswordConfirm: '',
    processType: 'send-login-reset-link',
    token: '',
    successMsg: 'We have updated your password'
  }),
  methods: {
    stepSkip: function(direction) {
      if (
        this.step === 1 &&
        direction === 'next' &&
        this.emailErrors.length === 0
      ) {
        this.$store
          .dispatch('AuthStore/sendLoginResetLink', this.userEmail)
          .then(() => {
            if (
              this.$store.state.AuthStore.loginResetProcessStatus !==
              'email-not-sent'
            ) {
              this.step = 3
            }
          })
      } else if (this.step === 2 && direction === 'next') {
        this.$store.dispatch('AuthStore/resetUserPassword', {
          token: this.token,
          userPassword: this.userPassword
        })
        this.step = 3
      } else if (this.step === 3 && direction === 'next') {
        this.$router.push('/').catch(err => {})
      }

      this.focusField()
    },
    onClickOkErrorDialog: function() {
      if (this.processType === 'send-login-reset-link') {
        this.step = 1
        this.focusField()
      } else {
        this.userPassword = ''
        this.userPasswordConfirm = ''
        this.$router.push('/').catch(err => {})
      }
    },
    focusField: function() {
      switch (this.step) {
        case 1:
          setTimeout(() => {
            this.$nextTick(this.$refs.userEmailInputField.focus)
          }, 500)
          break
        case 2:
          setTimeout(() => {
            this.$nextTick(this.$refs.userPasswordInputField.focus)
          }, 500)
          break
        default:
      }
    }
  },
  validations: {
    userEmail: {
      email,
      required
    },
    userPassword: {
      required,
      minLength: minLength(5)
    },
    userPasswordConfirm: {
      sameAsPassword: sameAs('userPassword')
    }
  },
  computed: {
    currentTitle() {
      switch (this.step) {
        case 1:
          return 'Reset password'
        case 2:
          return 'Enter a new password'
        case 3:
          return ''
        default:
          return ''
      }
    },
    emailErrors() {
      const errors = []
      if (!this.$v.userEmail.$dirty) return errors
      !this.$v.userEmail.email && errors.push('Must be valid e-mail')
      !this.$v.userEmail.required && errors.push('E-mail is required')
      return errors
    },
    passwordErrors() {
      const errors = []
      if (!this.$v.userPassword.$dirty) return errors
      !this.$v.userPassword.required && errors.push('Password is required')
      !this.$v.userPassword.minLength &&
        errors.push('Password must be at leas 6 characters')
      return errors
    },
    confirmPasswordErrors() {
      const errors = []
      if (!this.$v.userPasswordConfirm.$dirty) return errors
      !this.$v.userPasswordConfirm.sameAsPassword &&
        errors.push('Passwords must be identical')
      return errors
    },
    nextButtonDisabled() {
      switch (this.step) {
        case 1:
          if (this.userEmail === '' && this.emailErrors.length === 0) {
            return true
          } else {
            return this.emailErrors.length !== 0
          }
        case 2:
          if (this.userPassword === '' && this.passwordErrors.length === 0) {
            return true
          } else {
            return (
              this.passwordErrors.length !== 0 ||
              this.confirmPasswordErrors.length !== 0
            )
          }
        case 3:
          return false
        default:
          return true
      }
    },
    nextButtonLabel() {
      switch (this.step) {
        case 1:
          return 'Submit'
        case 2:
          return 'Submit'
        case 3:
          return 'OK'
        default:
          return ''
      }
    },
    processLoaderIsVisible() {
      let retVal = true
      if (
        this.processType === 'send-login-reset-link' &&
        this.$store.state.AuthStore.loginResetProcessStatus &&
        this.$store.state.errors.length === 0
      ) {
        retVal = false
      } else if (
        this.processType === 'reset-login-password' &&
        this.$store.state.errors.length === 0
      ) {
        retVal = false
      }
      return retVal
    }
  },
  created: function() {
    if (this.$route.query['token']) {
      this.step = 2
      this.processType = 'reset-login-password'
      this.token = this.$route.query['token']
      //this.$store.dispatch('verifyLoginResetCode', this.$route.query["verification-code"])
    }
    if (this.$route.query['message']) {
      this.successMsg = this.$route.query['message']
    } else {
      this.successMsg = 'We have updated your password'
    }
  },
  mounted: function() {
    this.focusField()
  }
}
</script>

<style scoped></style>
